import React, { useState } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  // DrawerClose,
} from '@/components/ui/drawer'
import { Button } from '../ui/button'
import { X, Plus, Pencil, Minus, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { cn } from '@/lib/styled'
import { requester } from '@/services/api'
import toast from 'react-hot-toast'
import { postService } from '@/services/posts/request'
import { Cat } from '@/type/cat'

const NewPost = (props: {
  selectedCat: Cat
  show: [boolean, (state: boolean) => void]
  onRequestSuccess: () => void
}) => {
  const { selectedCat, onRequestSuccess } = props
  const [show, setShow] = props.show
  const [imagesPreview, setImagesPreview] = useState<
    {
      file: File
      preview: string
    }[]
  >([])


  const {
    register,
    handleSubmit,
    // watch,
    // setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<{
    content: string
  }>({
    defaultValues: {
      content: '',
    },
    resolver: zodResolver(
      z.object({
        content: z
          .string({
            required_error: 'Content is required',
          })
          .min(1, {
            message: 'Content must be at least 1 character',
          }),
      })
    ),
  })

  // const photos = watch('photos')

  const onSubmit = async (data: { content: string }) => {
    let imagesToUpload: string[] = []
    try {
      await Promise.all(imagesPreview.map(async (image) => {
        let fileUrl = ''
        if (!!image.file.size) {
          const upload: any = await requester.post('/uploads', {
            fileName: image.file.name,
            contentType: image.file.type,
            metadata: {},
          })
  
          const { url, fields } = upload
  
          if (!url || !fields) {
            throw new Error('Upload file error: Invalid presigned URL response')
          }
          await fetch(url, { method: 'PUT', body: image.file })
          fileUrl = `${import.meta.env.VITE_CLOUDFLARE_BASE_URL}/${fields.key}`
          imagesToUpload.push(fileUrl)
          }
        })
      )
  
      await postService.createPost(selectedCat.id, {
        content: data.content,
        images: imagesToUpload,
      })
      await toast.success('Post created successfully')
      setShow(false)
      reset()
      setImagesPreview([])
      onRequestSuccess()
    } catch (error) {
      console.log(error)
      toast.error('Failed to create post')
    }
  }

  const handleUploadMultipleImages = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Define allowed image types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']

    // Create a single reader outside the loop
    const reader = new FileReader()
    let currentIndex = 0

    // Process files sequentially to avoid race conditions
    const processNextFile = () => {
      if (currentIndex >= files.length) return

      const file = files[currentIndex]

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          `Invalid file type: ${file.name}. Only JPG and PNG allowed.`
        )
        currentIndex++
        processNextFile()
        return
      }

      // Process file size
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error(`File too large: ${file.name}. Maximum size is 5MB.`)
        currentIndex++
        processNextFile()
        return
      }

      reader.onloadend = () => {
        setImagesPreview((prev) => [
          ...prev,
          {
            file: file,
            preview: reader.result as string,
          },
        ])

        // Move to next file
        currentIndex++
        processNextFile()
      }

      reader.onerror = () => {
        toast.error(`Error reading file: ${file.name}`)
        currentIndex++
        processNextFile()
      }

      reader.readAsDataURL(file)
    }

    // Start processing
    processNextFile()
  }

  return (
    <Drawer
      open={show}
      onOpenChange={() => {
        setShow(false)
        reset()
      }}
      onClose={() => {
        setShow(false)
        reset()
        setImagesPreview([])
      }}
    >
      <DrawerContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='w-full h-dvh max-w-sm mx-auto p-4 sm:p-0 flex flex-col gap-4'
        >
          <DrawerHeader className=''>
            <X
              className='absolute top-10 left-4 rounded-full border border-black'
              onClick={() => setShow(false)}
            />
            <DrawerTitle className='text-md font-bold'>
              Write a post
            </DrawerTitle>
          </DrawerHeader>
          <DrawerDescription asChild>
            <div className='flex flex-col gap-2'>
              {/* starts:: content input */}
              <div className='flex flex-col gap-2'>
                <p className='text-sm text-muted-foreground'>
                  <span className='font-bold pr-1'>user 123</span>
                  <span className='text-muted-foreground text-gray-400'>
                    {new Date().toLocaleDateString()}
                  </span>
                </p>
                <textarea
                  {...register('content')}
                  rows={5}
                  placeholder='tell others how the cat is doing...'
                  className={cn(
                    'w-full border border-gray-300 rounded-md p-3 focus:outline-none',
                    errors.content && 'border-red-500'
                  )}
                />
                {errors.content && (
                  <p className='text-red-500 text-sm'>
                    {errors.content.message}
                  </p>
                )}
                <input
                  id='multiple-image-upload'
                  type='file'
                  multiple
                  className='hidden'
                  onChange={handleUploadMultipleImages}
                />
              </div>
              <Button
                type='button'
                variant='secondary'
                className='w-full rounded-lg bg-[#EFEFEF] py-5'
                onClick={() =>
                  document.getElementById('multiple-image-upload')?.click()
                }
              >
                <Plus className='w-4 h-4' />
                <span className='text-sm rounded-lg'>Add photos</span>
              </Button>
              {/* ends:: content input */}

              {/* starts:: displayed images */}
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 overflow-y-auto max-h-[400px]'>
                {imagesPreview.length > 0 ? (
                  imagesPreview.map((photo, index: number) => (
                    <div key={index} className='relative'>
                      <img
                        src={photo.preview}
                        alt='photo'
                        className='w-full h-full object-cover rounded-md'
                      />
                      <Minus
                        className='absolute top-1 right-1 rounded-full border border-black bg-gray-100'
                        onClick={() =>
                          setImagesPreview(
                            imagesPreview.filter((_, i) => i !== index)
                          )
                        }
                      />
                    </div>
                  ))
                ) : (
                  <p className='col-span-full text-sm text-gray-400 text-center py-5'>
                    No images added
                  </p>
                )}
              </div>
              {/* ends:: displayed images */}

              {/* starts:: confirmation button */}
              <div className='mt-auto'>
                <Button
                  type='submit'
                  variant='primary'
                  className='w-full rounded-lg px-2 py-6 border-2 border-white drop-shadow-lg text-md'
                >
                  {isSubmitting ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <>
                      <Pencil className='w-4 h-4' />
                      <span>Post</span>
                    </>
                  )}
                </Button>
              </div>
              {/* ends:: confirmation button */}
            </div>
          </DrawerDescription>
        </form>
      </DrawerContent>
    </Drawer>
  )
}

export default NewPost
